import React from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createRecipeStyles } from "./styles";
import { CompositionIcon } from "../ui/RecipeIcons";
import { getStaticAssetPath } from "../../utils/staticAssets";
import { FetchedRecipe } from "../../types/recipe";

// Props for RecipeComposition component
interface RecipeCompositionProps {
  recipe: FetchedRecipe;
}

const RecipeComposition: React.FC<RecipeCompositionProps> = ({ recipe }) => {
  const theme = useTheme();
  const styles = createRecipeStyles(theme);

  // Fixed card dimensions per breakpoint
  const CARD_W = { xs: 100, sm: 100, md: 100, lg: 120 };
  const CARD_H = { xs: 230, sm: 230, md: 230, lg: 230 };
  const MEDIA_H = { xs: 30, sm: 30, md: 40, lg: 40 };

  return (
    <Paper elevation={0} sx={{ ...styles.page.sectionCard, minHeight: 380 }}>
      {/* Section header with icon and title */}
      <Box sx={styles.page.sectionHeader}>
        <CompositionIcon sx={{ fontSize: 36, mr: 1 }} />
        <Typography
          variant="h5"
          sx={{
            ...styles.page.sectionTitle,
            ml: 2,
            color: theme.palette.materiom.seaweed,
          }}
        >
          Composition
        </Typography>
      </Box>

      {/* Cards never stretch; they wrap with fixed width/height */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {(Array.isArray(recipe.data.ingredients) ? recipe.data.ingredients : [])
          .filter(
            (item) =>
              item &&
              typeof item.label === "string" &&
              item.value &&
              typeof item.value.amount !== "undefined" &&
              item.unit &&
              typeof item.unit.label === "string"
          )
          .map((item, key) => (
            <Box
              key={key}
              sx={{
                flex: "0 0 auto",
                width: CARD_W,
                height: CARD_H,
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {/* Ingredient image */}
                <CardMedia
                  component="img"
                  image={
                    item.coverImage
                      ? getStaticAssetPath(item.coverImage)
                      : "/static/img/placeholder.png"
                  }
                  alt={item.label || "Ingredient"}
                  sx={{
                    height: MEDIA_H,
                    width: "auto",
                    objectFit: "contain",
                    bgcolor: "grey.50",
                  }}
                />

                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    p: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: theme.palette.materiom.seaweed,
                      textAlign: "left",
                      whiteSpace: "normal",
                      overflow: "visible",
                      textOverflow: "unset",
                      wordBreak: "break-word",
                    }}
                    title={item.label || ""}
                  >
                    {item.label || "Unnamed ingredient"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      textAlign: "left",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={
                      item.value && item.unit
                        ? `${item.value.amount ?? ""} ${item.unit.label ?? ""}`
                        : ""
                    }
                  >
                    {item.value && item.unit
                      ? `${item.value.amount ?? ""} ${item.unit.label ?? ""}`
                      : ""}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
      </Box>
    </Paper>
  );
};

export default RecipeComposition;
